"use client";
/**
 * ChatWindow — top-level composition component for the ASK conversation view.
 * Track C2.
 *
 * Wires together: SessionSidebar + message list + ChatInput + welcome screen.
 */
import { useEffect, useRef, useCallback, useState, useMemo, memo } from "react";
import { Menu, AlertTriangle, RefreshCw, Package, Sparkles, Printer, Check, X, History, ChevronRight } from "lucide-react";
import { useChat } from "@/modes/ask/hooks/useChat";
import { useSessions } from "@/modes/ask/hooks/useSessions";
import { isPaidProfile } from "@/shared/types";
import type { Message } from "@/modes/ask/types";
import { SessionSidebar } from "./SessionSidebar";
import { MessageBubble } from "./MessageBubble";
import { ChatInput, type ChatInputHandle } from "./ChatInput";
import { ConversationNavPanel } from "./ConversationNavPanel";
import { SessionLimitModal } from "./SessionLimitModal";
import { SessionLimitBanner } from "./SessionLimitBanner";
import { MessageSearch, type MessageSearchState } from "./MessageSearch";
import { MemoryMenu } from "./MemoryMenu";
import { PrintDocument } from "./PrintDocument";
import { useAuthContext } from "@/platform/auth/AuthContext";

/** Formatted "Today / Yesterday / N days ago / date" label for a session. */
function relativeDay(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

// Memoized bubble so stepping between search matches does not re-render every
// message's markdown — the active ring lives on the wrapper div in this file,
// while bubble content only re-renders when a prop really changes.
const MemoMessageBubble = memo(MessageBubble);

const PRESET_PROMPTS = [
  "Explain supply chain optimization strategies",
  "How to improve inventory management?",
  "Best practices for procurement processes",
  "What is demand forecasting?",
  "Create a flowchart showing the order-to-cash process",
  "Diagram the procurement decision process",
];

interface ChatWindowProps {
  /** Pre-loaded session ID from URL (undefined = new conversation) */
  sessionId?: string;
}

export function ChatWindow({ sessionId: urlSessionId }: ChatWindowProps) {
  // Track the "active" session in local state so that clicking New Chat clears it
  // without requiring a full page reload (urlSessionId is a prop that won't change).
  const [activeSessionId, setActiveSessionId] = useState<string | undefined>(urlSessionId);

  const { user, profile, loading: authLoading } = useAuthContext();
  const isAuthed = !authLoading && !!user;

  // Stable passthrough so useChat can trigger an auto "new chat" when a session
  // completes; the real handler is defined below (it needs useChat's return).
  const autoNewChatRef = useRef<(text?: string) => void>(() => {});
  const onAutoNewChat  = useCallback((text?: string) => autoNewChatRef.current(text), []);

  const {
    messages,
    isLoading,
    error,
    sendMessage,
    regenerateMessage,
    loadConversation,
    clearError,
    clearSession,
    stop,
    sessionsUsed,
    sessionsLimit,
    sessionLimitReached,
    showSessionLimit,
    setShowSessionLimit,
  } = useChat(activeSessionId, { onAutoNewChat });

  const {
    sessions,
    isLoading: sessionsLoading,
    reload: reloadSessions,
    toggleStar,
    toggleArchive,
    renameSession,
    removeSession,
  } = useSessions();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile,         setIsMobile]         = useState(false);
  const [mobileMenuOpen,   setMobileMenuOpen]   = useState(false);

  // Sticky in-chat message search state.
  const [searchState, setSearchState] = useState<MessageSearchState>({
    open: false,
    query: "",
    matches: [],
    active: null,
    activeMessage: null,
    activeOccurrence: null,
    totalOccurrences: 0,
    navCount: 0,
  });

  const chatInputRef     = useRef<ChatInputHandle>(null);
  const bottomRef        = useRef<HTMLDivElement>(null);
  const scrollAreaRef    = useRef<HTMLDivElement>(null);
  const prevLenRef       = useRef(messages.length);
  const prevLoadingRef   = useRef(isLoading);

  // ── Inline edit of a user message ────────────────────────────────────────

  const [editingId,  setEditingId]  = useState<string | null>(null);
  const [editValue,  setEditValue]  = useState("");

  // ── Print / Save-as-PDF state ────────────────────────────────────────────

  const [printState, setPrintState] = useState<
    { variant: "full" } | { variant: "single"; message: Message } | null
  >(null);

  // Build one ref per user question (role === "user") to drive the nav panel.
  // We derive indices of user messages so the ref array is stable in length.
  const userMessageIndices = useMemo(
    () => messages.reduce<number[]>((acc, msg, i) => {
      if (msg.role === "user") acc.push(i);
      return acc;
    }, []),
    [messages]
  );

  // Keep a stable map of message-id → ref so refs persist across re-renders.
  const questionRefMap = useRef<Map<string, React.RefObject<HTMLDivElement | null>>>(new Map());

  const questionRefs = useMemo(() => {
    return userMessageIndices.map((msgIndex) => {
      const id = messages[msgIndex].id;
      if (!questionRefMap.current.has(id)) {
        questionRefMap.current.set(id, { current: null });
      }
      return questionRefMap.current.get(id)!;
    });
  }, [userMessageIndices, messages]);

  // ── Responsive detection ────────────────────────────────────────────────

  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Load existing conversation ──────────────────────────────────────────

  // Deep link from a sidebar search hit: /ask/{id}?find=term hands the term to
  // the in-chat search so it opens scrolled to the first match.
  //
  // `find`, not `q`: `?q=` is the homepage hero-search contract, handled below
  // (fills the composer and auto-submits). Reading it here too destroyed the
  // session id and bounced users to /login. Read from window (not
  // useSearchParams) to avoid a Suspense boundary, then consumed so a refresh
  // does not replay the jump.
  const [deepLinkQuery, setDeepLinkQuery] = useState("");

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("find");
    if (!q) return;
    // Deferred a frame: this must land AFTER hydration, otherwise the search
    // panel would render expanded on the client against a collapsed server
    // render. Consume the param so a refresh does not replay the jump.
    const raf = requestAnimationFrame(() => {
      setDeepLinkQuery(q);
      window.history.replaceState(null, "", window.location.pathname);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (activeSessionId) {
      loadConversation(activeSessionId);
    } else {
      clearSession();
    }
  }, [activeSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Deep-link from hero search (?q=) → fill + instantly submit ──────────

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (!q) return;
    // Clean the URL so a refresh won't re-submit. Strip only the query — never
    // rebuild the path from activeSessionId, which is still "" on a cold load
    // and would silently drop a /ask/{sessionId} from the URL.
    window.history.replaceState(null, "", window.location.pathname);
    chatInputRef.current?.setMessage(q);
    // Defer submit until after the message state is applied.
    setTimeout(() => chatInputRef.current?.submit(), 0);
  }, [activeSessionId]);

  // ── Clean up pending-submit flag on auth ─────────────────────────────────
  // The flag is no longer needed once the user is back on /ask — the draft
  // is already restored by ChatInput from its own localStorage key.

  useEffect(() => {
    if (!isAuthed) return;
    localStorage.removeItem("ask_pending_submit");
  }, [isAuthed]);

  // ── Auto-scroll to bottom on new messages ──────────────────────────────

  useEffect(() => {
    if (messages.length > prevLenRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevLenRef.current = messages.length;
  }, [messages]);

  // ── Auto-focus the input when a run finishes ─────────────────────────────
  // The textarea stays editable during a run, so once it ends, the next
  // logical step (a correction / follow-up) starts by just typing.

  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      chatInputRef.current?.focus();
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  // ── Print / Save-as-PDF (document format of the whole chat) ─────────────

  const handlePrintAll = useCallback(() => {
    setPrintState({ variant: "full" });
  }, []);

  const handlePrintMessage = useCallback((message: Message) => {
    setPrintState({ variant: "single", message });
  }, []);

  // Open the print dialog once the portaled document is rendered.
  // Mermaid diagrams render asynchronously, so wait long enough for them to
  // finish before showing the dialog — otherwise diagrams print blank.
  useEffect(() => {
    if (!printState) return;
    const t = setTimeout(() => window.print(), 700);
    return () => clearTimeout(t);
  }, [printState]);

  // Reset the print document after the print flow finishes (or on timeout).
  useEffect(() => {
    if (!printState) return;
    const cleanup = () => setPrintState(null);
    window.addEventListener("afterprint", cleanup);
    const t = setTimeout(cleanup, 60_000);
    return () => {
      window.removeEventListener("afterprint", cleanup);
      clearTimeout(t);
    };
  }, [printState]);

  // ── Inline edit of a user message ───────────────────────────────────────

  const handleEditStart = useCallback((message: Message) => {
    setEditingId(message.id);
    setEditValue(message.content.replace(/<br\s*\/?>/gi, "\n"));
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingId(null);
    setEditValue("");
  }, []);

  const handleEditSave = useCallback(() => {
    const message = messages.find((m) => m.id === editingId);
    if (!message) return;
    const newContent = editValue.trim().length
      ? editValue.replace(/\n/g, "<br>")
      : message.content;
    setEditingId(null);
    setEditValue("");
    void regenerateMessage({ ...message, content: newContent });
  }, [editingId, editValue, messages, regenerateMessage]);

  // ── Suggested prompt → fill input ──────────────────────────────────────

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ prompt?: string; text?: string }>).detail;
      const prompt = detail?.prompt ?? detail?.text;
      if (prompt) chatInputRef.current?.setMessage(prompt);
    };
    window.addEventListener("selectSuggestedPrompt", handler);
    return () => window.removeEventListener("selectSuggestedPrompt", handler);
  }, []);

  // ── Send ────────────────────────────────────────────────────────────────

  const handleSend = useCallback(
    async (content: string, attachments?: File[], mode: "fast" | "expert" = "fast") => {
      // Unauthenticated: ChatInput has already cleared the draft from
      // localStorage before calling onSend, so we restore it here using the
      // same key ChatInput uses (DRAFT_KEY = "ask_message"). Then we set the
      // pending-submit flag and redirect to login.
      if (!isAuthed) {
        // content arrives with <br> substituted for newlines — store the
        // display-safe version so it round-trips correctly.
        localStorage.setItem("ask_message", content);
        localStorage.setItem("ask_pending_submit", "1");
        window.location.href = `/login?redirect_to=${encodeURIComponent("/ask")}`;
        return;
      }
      await sendMessage(content, attachments, mode);
      await reloadSessions();
    },
    [isAuthed, sendMessage, reloadSessions]
  );

  const isWelcome = messages.length === 0 && !activeSessionId;
  const isPro = isPaidProfile(profile?.subscription);

  // Most recently updated conversation (sessions come ordered by updatedAt desc).
  // Shown on the welcome screen as a one-click "continue where you left off".
  const continueSession = isAuthed && sessions.length > 0 ? sessions[0] : null;

  // Print document content selection
  const printTitle = useMemo(() => {
    if (printState?.variant === "single") {
      return printState.message.role === "assistant" ? "sensei. — Answer" : "You — Message";
    }
    const firstUser = messages.find((m) => m.role === "user");
    const t = firstUser?.content.replace(/<[^>]*>/g, "").trim() ?? "Conversation";
    return t.substring(0, 60);
  }, [printState, messages]);

  const printMessages = printState?.variant === "single" ? [printState.message] : messages;

  // ── New chat ────────────────────────────────────────────────────────────

  const handleNewChat = useCallback(() => {
    if (!isPro && sessionLimitReached) {
      setShowSessionLimit(true);
      return;
    }
    clearSession();
    setActiveSessionId(undefined);
    window.history.pushState(null, "", "/ask");
    if (isMobile) setMobileMenuOpen(false);
  }, [clearSession, isMobile, isPro, sessionLimitReached, setShowSessionLimit]);

  // ── Auto new chat after a completed session (token limit) ────────────────────
  // Mirrors handleNewChat. The carried question is re-submitted by useChat, so
  // this handler only resets the view (and guards the free 5-session cap).

  const handleAutoNewChat = useCallback(() => {
    if (!isPro && sessionLimitReached) {
      setShowSessionLimit(true);
      return;
    }
    clearSession();
    setActiveSessionId(undefined);
    window.history.pushState(null, "", "/ask");
    if (isMobile) setMobileMenuOpen(false);
  }, [clearSession, isMobile, isPro, sessionLimitReached, setShowSessionLimit]);

  useEffect(() => {
    autoNewChatRef.current = handleAutoNewChat;
  }, [handleAutoNewChat]);

  // ── Preset prompt ───────────────────────────────────────────────────────

  const handlePresetClick = useCallback((prompt: string) => {
    chatInputRef.current?.setMessage(prompt);
  }, []);

  // ── Delete session (navigate away if active) ────────────────────────────

  const handleDelete = useCallback(
    async (id: string) => {
      await removeSession(id);
      if (id === activeSessionId) {
        clearSession();
        setActiveSessionId(undefined);
        window.history.pushState(null, "", "/ask");
      }
    },
    [removeSession, activeSessionId, clearSession]
  );

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="flex h-[calc(100vh-64px)] bg-background overflow-hidden">
      {/* Mobile overlay */}
      {isMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 top-16 bg-black/50 z-20 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile hamburger — hidden for unauthenticated users on the welcome screen */}
      {isMobile && !mobileMenuOpen && (isAuthed || !isWelcome) && (
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="fixed top-20 left-4 z-50 md:hidden p-2 rounded-xl bg-background/80 backdrop-blur-sm border border-border shadow-lg"
          aria-label="Open sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}

      {/* Sidebar — hidden for unauthenticated users on the welcome screen */}
      {(isAuthed || !isWelcome) && (
        <div
          className={`h-full z-30 transition-transform duration-300 ease-in-out ${
            isMobile
              ? `fixed top-16 left-0 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}`
              : "relative"
          }`}
        >
          <SessionSidebar
            sessions={sessions}
            isLoading={sessionsLoading}
            activeSessionId={activeSessionId}
            isCollapsed={!isMobile && sidebarCollapsed}
            isMobile={isMobile}
            onSelectSession={(id) => { window.location.href = `/ask/${id}`; }}
            onNewChat={handleNewChat}
            onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
            onCloseMobile={() => setMobileMenuOpen(false)}
            onToggleStar={toggleStar}
            onToggleArchive={toggleArchive}
            onRename={renameSession}
            onDelete={handleDelete}
            sessionsUsed={isPro ? undefined : sessionsUsed}
            sessionsLimit={isPro ? undefined : sessionsLimit}
            sessionLimitReached={isPro ? undefined : sessionLimitReached}
          />
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative">
        {/* Error bar */}
        {error && (
          <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-red-500/10 border-b border-red-500/20 text-sm">
            <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
            <span className="text-red-300 flex-1">{error.message}</span>
            <button
              onClick={error.retry}
              className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 font-medium"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
            <button
              onClick={clearError}
              className="text-red-400 hover:text-red-300 text-xs"
            >
              Dismiss
            </button>
          </div>
        )}

        {isWelcome ? (
          /* ── Welcome screen ── */
          <div className="flex-1 flex flex-col overflow-y-auto">
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
              <div className="w-full max-w-3xl mx-auto text-center space-y-6 flex-1 flex flex-col justify-center">
                {/* Heading */}
                <div>
                  <div className="flex items-center justify-center mx-auto mb-4" style={{ width: 60, height: 60 }}>
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="url(#ask-hero-grad)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <defs>
                        <linearGradient id="ask-hero-grad" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#5B6CFF"/>
                          <stop offset="100%" stopColor="#EC4899"/>
                        </linearGradient>
                      </defs>
                      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
                      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
                    </svg>
                  </div>
                  <h1 className="mb-3" style={{ fontSize: "clamp(36px, 4.5vw, 60px)", fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1 }}>
                    <span className="grad-text">sensei.</span> Ask
                  </h1>
                  <p className="text-muted-foreground mx-auto leading-relaxed" style={{ fontSize: 17, maxWidth: "60ch", marginBottom: 0 }}>
                    Your intelligent supply chain consultant. Ask me anything about procurement, logistics, planning, and operations.
                  </p>
                </div>

                {/* Preset prompt grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PRESET_PROMPTS.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => handlePresetClick(prompt)}
                      className="text-left p-4 rounded-xl border border-border bg-card hover:border-indigo-500/30 hover:shadow-md hover:bg-indigo-500/5 transition-all duration-300"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                          <Package className="h-4 w-4 text-indigo-400" />
                        </div>
                        <p className="text-sm leading-snug">{prompt}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* CTA badge — only shown to authenticated users */}
                {isAuthed && (
                  <div>
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20">
                      <Sparkles className="h-4 w-4 text-indigo-400" />
                      <span className="text-sm text-indigo-300 font-medium">
                        Start your supply chain conversation
                      </span>
                    </div>
                  </div>
                )}

                {/* Session limit banner — welcome screen */}
                {!isPro && sessionLimitReached && (
                  <div className="w-full">
                    <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-indigo-500/10 to-pink-500/10 border border-indigo-500/20 px-4 py-2.5 text-left">
                      <p className="text-sm text-foreground">
                        You&apos;ve reached the session limit.{" "}
                        <a href="/pricing" className="font-semibold text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors">
                          Upgrade to Pro
                        </a>{" "}
                        to unlock unlimited sessions.
                      </p>
                    </div>
                  </div>
                )}

                {/* Continue where you left off — one-click resume of the
                    most recent conversation (authenticated users only) */}
                {continueSession && (
                  <button
                    type="button"
                    onClick={() => { window.location.href = `/ask/${continueSession.id}`; }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-indigo-500/25 bg-indigo-500/5 hover:bg-indigo-500/10 hover:border-indigo-500/50 transition-all duration-300 text-left cursor-pointer"
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <History className="h-4 w-4 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300 mb-0.5">
                        Continue where you left off
                      </p>
                      <p className="text-sm font-medium text-foreground truncate">{continueSession.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{continueSession.lastMessage}</p>
                    </div>
                    <span className="text-[11px] text-muted-foreground flex-shrink-0">
                      {relativeDay(continueSession.updatedAt)}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                )}

                {/* Input — inline on welcome screen */}
                {/* Input — always active; unauthenticated submit redirects to login */}
                <ChatInput
                  ref={chatInputRef}
                  onSend={handleSend}
                  onStop={stop}
                  isLoading={isLoading}
                  sessionId={activeSessionId}
                />
              </div>
            </div>
          </div>
        ) : (
          /* ── Chat view ── */
          <>
            <div
              ref={scrollAreaRef}
              className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-thumb]:rounded-full"
            >
              <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
                {messages.map((msg, i) => {
                  const isLast      = i === messages.length - 1;
                  const isStreaming = isLast && isLoading && msg.role === "assistant";
                  const isUser      = msg.role === "user";
                  // Find the question-ref for this user message (if any)
                  const qIndex      = isUser ? userMessageIndices.indexOf(i) : -1;
                  const qRef        = qIndex >= 0 ? questionRefs[qIndex] : null;

                  // In-chat search highlight: matches stay visible, the active one is emphasized.
                  // NOTE: no padding/margin/transition here — layout shifts mid-scroll
                  // cancel smooth scrolling and make Enter feel like it "does nothing".
                  const isSearchMatch = searchState.matches.includes(i);
                  const isSearchActive = searchState.activeMessage === i;
                  const searchWrapCls = !searchState.open
                    ? ""
                    : isSearchActive
                      ? "relative rounded-xl ring-2 ring-indigo-400 bg-indigo-500/10"
                      : isSearchMatch
                        ? "rounded-xl ring-1 ring-amber-400/60 bg-amber-400/5"
                        : "";

                  return (
                    <div
                      key={msg.id}
                      ref={qRef as React.RefObject<HTMLDivElement> | null}
                      data-search-index={String(i)}
                      className={searchWrapCls}
                    >
{/* Flash on the active match, replayed on EVERY navigation
                      step (remount via navCount) so it is always visible. */}
                  {isSearchActive && (
                    <div key={searchState.navCount} className="pointer-events-none absolute inset-0 rounded-xl search-flash" />
                  )}
                      {editingId === msg.id ? (
                    <div className="rounded-2xl border border-indigo-500/40 bg-muted/40 p-3">
                      <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            void handleEditSave();
                          }
                          if (e.key === "Escape") handleEditCancel();
                        }}
                        placeholder="Edit your question…"
                        className="w-full min-h-[70px] max-h-[240px] resize-y bg-transparent text-sm leading-relaxed text-foreground outline-none focus:ring-0"
                      />
                      <div className="mt-2 flex justify-end items-center gap-2">
                        <button
                          type="button"
                          onClick={handleEditCancel}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted/60 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" /> Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleEditSave()}
                          disabled={!editValue.trim()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: "linear-gradient(135deg, #5B6CFF, #EC4899)" }}
                        >
                          <Check className="h-3.5 w-3.5" /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <MemoMessageBubble
                      message={msg}
                      isStreaming={isStreaming}
                      sessionId={activeSessionId}
                      highlightQuery={searchState.open && searchState.query ? searchState.query : undefined}
                      activeOccurrence={isSearchActive ? searchState.activeOccurrence ?? undefined : undefined}
                      onEditMessage={isUser ? handleEditStart : undefined}
                      onPrintMessage={msg.role === "assistant" && !isStreaming ? handlePrintMessage : undefined}
                    />
                  )}
                    </div>
                  );
                })}

                {/* Typing indicator while loading before first chunk
                {isLoading && messages[messages.length - 1]?.content === "Loading..." && (
                  <div className="flex items-center gap-2 pl-12 text-muted-foreground text-sm">
                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                )} */}

                <div ref={bottomRef} />
              </div>
            </div>

            {/* Sticky in-chat message search — top-right of chat panel */}
            <MessageSearch
              messages={messages}
              scrollContainerRef={scrollAreaRef as React.RefObject<HTMLDivElement | null>}
              onStateChange={setSearchState}
              initialQuery={deepLinkQuery}
            />

            {/* Cross-session chat memory control — next to search */}
            <MemoryMenu />

            {/* Print / Save-as-PDF — only when there is something to print */}
            {messages.length > 0 && (
              <div className="absolute top-3 right-28 z-40">
                <button
                  type="button"
                  onClick={handlePrintAll}
                  aria-label="Print or save as PDF"
                  title="Print / Save as PDF"
                  className="h-9 w-9 rounded-xl border border-border bg-card text-muted-foreground shadow-sm flex items-center justify-center transition-colors hover:bg-accent hover:text-foreground"
                >
                  <Printer className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Floating conversation nav — desktop only */}
            {!isMobile && (
              <ConversationNavPanel
                questionRefs={questionRefs as React.RefObject<HTMLElement | null>[]}
                scrollContainerRef={scrollAreaRef as React.RefObject<HTMLElement | null>}
              />
            )}

            {/* Input — pinned at bottom; unauthenticated submit redirects to login */}
            <div className="flex-shrink-0 pb-4 pt-3 border-t border-border/30">
              {!isPro && sessionLimitReached && <SessionLimitBanner />}
              <ChatInput
                ref={chatInputRef}
                onSend={handleSend}
                onStop={stop}
                isLoading={isLoading}
                sessionId={activeSessionId}
              />
            </div>
          </>
        )}
      </div>

      {/* Print document (portaled to <body>; only visible during print) */}
      {printState && <PrintDocument title={printTitle} messages={printMessages} />}

      {/* Session limit modal */}
      <SessionLimitModal
        isOpen={!isPro && showSessionLimit}
        onClose={() => setShowSessionLimit(false)}
      />
    </div>
  );
}
